import React, { useState, useEffect } from "react";

const EBITDACalculator = () => {
  const [revenue, setRevenue] = useState(1000000);
  const [cogs, setCogs] = useState(400000);
  const [operatingExpenses, setOperatingExpenses] = useState(200000);
  const [depreciation, setDepreciation] = useState(50000);
  const [amortization, setAmortization] = useState(20000);
  const [interestExpense, setInterestExpense] = useState(30000);
  const [taxRate, setTaxRate] = useState(25);
  const [currency, setCurrency] = useState("usd");
  const [result, setResult] = useState({
    ebitda: "0",
    category: "",
    interpretation: "",
    advice: "",
    ebitdaMargin: "",
    netIncome: "",
  });

  // EBITDA Calculation
  const calculateEBITDA = () => {
    let rev = parseFloat(revenue);
    let cost = parseFloat(cogs);
    let opex = parseFloat(operatingExpenses);
    let dep = parseFloat(depreciation);
    let amort = parseFloat(amortization);
    let interest = parseFloat(interestExpense);
    let tax = parseFloat(taxRate);

    if (isNaN(rev) || rev <= 0 || isNaN(cost) || cost < 0 || isNaN(opex) || opex < 0 || isNaN(dep) || dep < 0 || isNaN(amort) || amort < 0) return;

    // Calculate EBITDA: Revenue - COGS - Operating Expenses + Depreciation + Amortization
    // Or: Operating Income + Depreciation + Amortization
    const grossProfit = rev - cost;
    const operatingIncome = grossProfit - opex;
    const ebitdaValue = operatingIncome + dep + amort;

    // Calculate EBITDA Margin: (EBITDA / Revenue) × 100
    const ebitdaMargin = (ebitdaValue / rev) * 100;

    // Calculate EBIT (Earnings Before Interest and Taxes)
    const ebit = operatingIncome;

    // Calculate EBT (Earnings Before Tax)
    const ebt = ebit - interest;

    // Calculate Net Income
    const netIncome = ebt - (ebt * (tax / 100));

    // Determine category based on EBITDA margin
    let category = "";
    let interpretation = "";
    
    if (ebitdaMargin < 0) {
      category = "Negative EBITDA";
      interpretation = "Your business is operating at a loss before accounting for financing costs. This indicates fundamental operational challenges that need immediate attention.";
    } else if (ebitdaMargin < 10) {
      category = "Low Margin";
      interpretation = "Your EBITDA margin is below industry averages. This suggests tight operational efficiency and limited pricing power. Focus on cost optimization and revenue enhancement.";
    } else if (ebitdaMargin < 20) {
      category = "Average Margin";
      interpretation = "Your EBITDA margin is within typical industry ranges. This indicates reasonable operational efficiency with room for improvement through strategic initiatives.";
    } else if (ebitdaMargin < 30) {
      category = "Good Margin";
      interpretation = "You're achieving above-average EBITDA margins! This demonstrates strong operational efficiency, good cost management, and healthy pricing power.";
    } else if (ebitdaMargin < 40) {
      category = "Excellent Margin";
      interpretation = "Outstanding EBITDA margin! Your business shows exceptional operational efficiency, strong competitive positioning, and excellent scalability potential.";
    } else {
      category = "Exceptional Margin";
      interpretation = "Extraordinary EBITDA margin! This level suggests market leadership, significant competitive advantages, premium pricing power, or highly scalable operations.";
    }

    // Generate personalized advice
    let advice = "";
    if (ebitdaMargin < 0) {
      advice = "Immediate action required: Analyze cost structure, review pricing strategy, identify underperforming segments, and consider operational restructuring. Consult with financial advisors.";
    } else if (ebitdaMargin < 15) {
      advice = "Focus on operational efficiency: Reduce COGS through supplier negotiations, optimize operating expenses, implement automation, and explore revenue diversification opportunities.";
    } else if (ebitdaMargin < 25) {
      advice = "Continue optimizing operations while investing in growth. Consider technology upgrades, process improvements, and strategic investments that can scale without proportional cost increases.";
    } else {
      advice = "Maintain operational excellence while focusing on strategic growth. Reinvest in innovation, market expansion, and talent acquisition. Monitor margins during scaling to preserve profitability.";
    }

    const currencySymbol = currency === "usd" ? "$" : currency === "eur" ? "€" : currency === "gbp" ? "£" : "₹";
    
    const ebitdaDisplay = `${currencySymbol}${ebitdaValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    const ebitdaMarginDisplay = `${ebitdaMargin.toFixed(2)}%`;
    const netIncomeDisplay = `${currencySymbol}${netIncome.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    const grossProfitDisplay = `${currencySymbol}${grossProfit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    const operatingIncomeDisplay = `${currencySymbol}${operatingIncome.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    const ebitDisplay = `${currencySymbol}${ebit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    setResult({
      ebitda: ebitdaDisplay,
      category,
      interpretation,
      advice,
      ebitdaMargin: ebitdaMarginDisplay,
      netIncome: netIncomeDisplay,
      grossProfit: grossProfitDisplay,
      operatingIncome: operatingIncomeDisplay,
      ebit: ebitDisplay,
    });
  };

  useEffect(() => {
    calculateEBITDA();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
          </div>
          <h1>EBITDA Calculator</h1>
          <p>
            Calculate your EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) - a key metric used to evaluate operating performance and compare profitability across companies and industries.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Financial Data
              </h2>

              {/* Revenue Input */}
              <div className="input-group">
                <label>Total Revenue</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💰</span>
                  <input
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    className="calc-input"
                    placeholder="1000000"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2500000, 5000000, 10000000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setRevenue(val)}
                      className="quick-select-btn primary"
                    >
                      {val >= 1000000 ? `${val / 1000000}M` : `${val / 1000}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* COGS Input */}
              <div className="input-group">
                <label>Cost of Goods Sold (COGS)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📦</span>
                  <input
                    type="number"
                    value={cogs}
                    onChange={(e) => setCogs(e.target.value)}
                    className="calc-input"
                    placeholder="400000"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[200000, 400000, 600000, 800000, 1000000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setCogs(val)}
                      className="quick-select-btn primary"
                    >
                      {val >= 1000000 ? `${val / 1000000}M` : `${val / 1000}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Operating Expenses Input */}
              <div className="input-group">
                <label>Operating Expenses</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📊</span>
                  <input
                    type="number"
                    value={operatingExpenses}
                    onChange={(e) => setOperatingExpenses(e.target.value)}
                    className="calc-input"
                    placeholder="200000"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 200000, 300000, 400000, 500000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setOperatingExpenses(val)}
                      className="quick-select-btn success"
                    >
                      {val / 1000}K
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Includes SG&A (Selling, General & Administrative) expenses.
                </p>
              </div>

              {/* Depreciation Input */}
              <div className="input-group">
                <label>Depreciation</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📉</span>
                  <input
                    type="number"
                    value={depreciation}
                    onChange={(e) => setDepreciation(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[25000, 50000, 75000, 100000, 150000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setDepreciation(val)}
                      className="quick-select-btn success"
                    >
                      {val / 1000}K
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Non-cash expense for tangible asset deterioration.
                </p>
              </div>

              {/* Amortization Input */}
              <div className="input-group">
                <label>Amortization</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📄</span>
                  <input
                    type="number"
                    value={amortization}
                    onChange={(e) => setAmortization(e.target.value)}
                    className="calc-input"
                    placeholder="20000"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[10000, 20000, 30000, 50000, 75000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAmortization(val)}
                      className="quick-select-btn success"
                    >
                      {val / 1000}K
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Non-cash expense for intangible asset write-off.
                </p>
              </div>

              {/* Interest Expense Input */}
              <div className="input-group">
                <label>Interest Expense</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💳</span>
                  <input
                    type="number"
                    value={interestExpense}
                    onChange={(e) => setInterestExpense(e.target.value)}
                    className="calc-input"
                    placeholder="30000"
                    min="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[10000, 25000, 50000, 75000, 100000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setInterestExpense(val)}
                      className="quick-select-btn success"
                    >
                      {val / 1000}K
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Interest paid on debt and loans.
                </p>
              </div>

              {/* Tax Rate */}
              <div className="input-group">
                <label>Tax Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🏛️</span>
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                </div>
                <div className="quick-select-group">
                  {[15, 21, 25, 30, 35].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTaxRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Currency */}
              <div className="input-group">
                <label>Currency</label>
                <div className="quick-select-group">
                  {[
                    { value: "usd", label: "USD ($)" },
                    { value: "eur", label: "EUR (€)" },
                    { value: "gbp", label: "GBP (£)" },
                    { value: "inr", label: "INR (₹)" },
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`quick-select-btn ${currency === curr.value ? "primary" : "secondary"}`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateEBITDA} className="calculate-btn">
                Calculate EBITDA
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Financial Performance
              </h2>

              <div className="result-card">
                {/* EBITDA */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">EBITDA</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.ebitda}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Margin & Net Income */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">EBITDA Margin</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="8 12 12 16 16 12"></polyline>
                          <line x1="12" y1="8" x2="12" y2="16"></line>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.ebitdaMargin}</div>
                    <div className="result-subtext">profitability ratio</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Net Income</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.netIncome}</div>
                    <div className="result-subtext">after all expenses</div>
                  </div>
                </div>

                {/* Other Metrics */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Gross Profit</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                          <polyline points="17 6 23 6 23 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.grossProfit}</div>
                    <div className="result-subtext">revenue - COGS</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Operating Income</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="9" y1="9" x2="15" y2="9"></line>
                          <line x1="9" y1="15" x2="15" y2="15"></line>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.operatingIncome}</div>
                    <div className="result-subtext">EBIT</div>
                  </div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">What This Means</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500", marginBottom: "0.75rem" }}>
                    {result.interpretation}
                  </p>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.advice}
                  </p>
                </div>

                {/* EBITDA Margin Categories */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">EBITDA Margin Ranges</h3>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fee2e2", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Negative: Below 0%</div>
                    <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Operating losses - urgent action needed</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Low: 0-10%</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Below average operational efficiency</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Average: 10-20%</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Typical industry performance</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#d1fae5", borderRadius: "0.375rem", borderLeft: "4px solid #34d399" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Good: 20-30%</div>
                    <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Above-average operational strength</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dcfce7", borderRadius: "0.375rem", borderLeft: "4px solid #10b981" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Excellent: 30-40%</div>
                    <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Strong competitive position</div>
                  </div>
                  <div style={{ padding: "0.75rem", backgroundColor: "#fae8ff", borderRadius: "0.375rem", borderLeft: "4px solid #a855f7" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Exceptional: 40%+</div>
                    <div style={{ fontSize: "0.75rem", color: "#581c87" }}>Market leadership level</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding EBITDA</h3>
              <p>
                EBITDA (Earnings Before Interest, Taxes, Depreciation, and Amortization) is a measure of a company's operating performance. It shows how much profit a company makes from its core operations before accounting for capital structure, tax rates, and non-cash accounting items.
                This metric is particularly useful for comparing profitability between companies and industries because it eliminates the effects of financing and accounting decisions. Investors and analysts use EBITDA to assess operational efficiency and cash generation potential.
                EBITDA margin (EBITDA divided by revenue) helps understand what percentage of revenue translates into operating profit, making it easier to compare companies of different sizes and in different industries.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>EBITDA Formulas:</h4>
            <div className="formula-display">
              EBITDA = Revenue - COGS - Operating Expenses + Depreciation + Amortization
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Alternative: EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              EBITDA Margin = (EBITDA / Revenue) × 100
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Revenue</div>
                <div className="formula-param-value">Total sales income</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">COGS</div>
                <div className="formula-param-value">Direct production costs</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">OpEx</div>
                <div className="formula-param-value">Operating expenses</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">D&A</div>
                <div className="formula-param-value">Non-cash charges</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Why is EBITDA important?</h5>
              <p>
                EBITDA provides a clearer picture of operational profitability by removing the impact of financing decisions (interest), tax environments, and accounting methods (depreciation/amortization). It's widely used for company valuations, M&A analysis, and comparing businesses across different industries and geographies.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's a good EBITDA margin?</h5>
              <p>
                This varies by industry. Software companies often have 30-50% margins, while retail might be 5-10%. Generally, margins above 20% are considered strong. Compare your EBITDA margin to industry peers for meaningful insights. Consistency and growth trends are as important as absolute numbers.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the limitations of EBITDA?</h5>
              <p>
                EBITDA ignores capital expenditures, working capital changes, and actual cash tax payments. It can overstate cash flow, especially for capital-intensive businesses. Some companies manipulate EBITDA by excluding legitimate operating expenses. Always analyze EBITDA alongside cash flow and net income.
              </p>
            </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default EBITDACalculator;